import { useEffect, useRef, useCallback } from 'react';

const MERKABA_VERTICES = [
  [1,1,1], [1,-1,-1], [-1,1,-1], [-1,-1,1],
  [-1,-1,-1], [-1,1,1], [1,-1,1], [1,1,-1]
];

const MERKABA_EDGES = [
  [0,1],[0,2],[0,3],[1,2],[1,3],[2,3],
  [4,5],[4,6],[4,7],[5,6],[5,7],[6,7]
];

const RIPPLE_COLORS = ["#07FAEE", "#4A2FBD", "#36AAA4", "#AA367C"];

function CursorFollower() {
  const canvasRef = useRef(null);
  const trailEls = useRef([]);
  const pos = useRef({ x: -100, y: -100 });
  const target = useRef({ x: -100, y: -100 });
  const trailPos = useRef(Array.from({ length: 3 }, () => ({ x: -100, y: -100 })));
  const speed = useRef(0);
  const prev = useRef({ x: -100, y: -100 });
  const angles = useRef({ x: 0, y: 0, z: 0 });
  const isHovering = useRef(false);

  useEffect(() => {
    const els = [];
    for (let i = 0; i < 3; i++) {
      const el = document.createElement('div');
      el.className = 'cursor-trail';
      document.body.appendChild(el);
      els.push(el);
    }
    trailEls.current = els;
    return () => els.forEach(el => el.remove());
  }, []);

  const handleMouseMove = useCallback(e => {
    target.current.x = e.clientX;
    target.current.y = e.clientY;
    const dx = e.clientX - prev.current.x;
    const dy = e.clientY - prev.current.y;
    speed.current = Math.min(Math.sqrt(dx * dx + dy * dy), 30);
    prev.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseDown = useCallback(e => {
    const canvas = document.createElement('canvas');
    canvas.className = 'cursor-atom';
    const size = 130;
    canvas.width = size;
    canvas.height = size;
    canvas.style.left = `${e.clientX - size / 2}px`;
    canvas.style.top = `${e.clientY - size / 2}px`;
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    const sizeHalf = size / 2;
    const c1 = RIPPLE_COLORS[Math.floor(Math.random() * RIPPLE_COLORS.length)];
    const c2 = RIPPLE_COLORS[Math.floor(Math.random() * RIPPLE_COLORS.length)];
    const start = performance.now();
    const duration = 800;
    const trailLen = 18;

    let e1Trail = [], e2Trail = [];
    let a1 = 0, a2 = Math.PI;
    let r1 = 8, r2 = 8;

    function frame(now) {
      const t = Math.min((now - start) / duration, 1);
      const alpha = 1 - t;

      a1 += 0.09;
      a2 -= 0.09;
      r1 += 0.5;
      r2 += 0.5;

      const x1 = sizeHalf + Math.cos(a1) * r1;
      const y1 = sizeHalf + Math.sin(a1) * r1;
      const x2 = sizeHalf + Math.cos(a2) * r2;
      const y2 = sizeHalf + Math.sin(a2) * r2;

      canvas.style.left = `${pos.current.x - sizeHalf}px`;
      canvas.style.top = `${pos.current.y - sizeHalf}px`;

      e1Trail.push({ x: x1, y: y1 });
      e2Trail.push({ x: x2, y: y2 });
      if (e1Trail.length > trailLen) e1Trail.shift();
      if (e2Trail.length > trailLen) e2Trail.shift();

      ctx.clearRect(0, 0, size, size);

      [e1Trail, e2Trail].forEach((trail, idx) => {
        const col = idx === 0 ? c1 : c2;
        trail.forEach((p, i) => {
          const f = i / trail.length;
          ctx.globalAlpha = f * 0.5 * alpha;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 2 * f, 0, Math.PI * 2);
          ctx.fillStyle = col;
          ctx.fill();
        });
      });
      ctx.globalAlpha = 1;

      ctx.shadowColor = c1;
      ctx.shadowBlur = 14;
      ctx.beginPath();
      ctx.arc(x1, y1, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = c1;
      ctx.fill();

      ctx.shadowColor = c2;
      ctx.shadowBlur = 14;
      ctx.beginPath();
      ctx.arc(x2, y2, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = c2;
      ctx.fill();

      ctx.shadowBlur = 0;

      if (t < 1) {
        requestAnimationFrame(frame);
      } else {
        canvas.remove();
      }
    }
    requestAnimationFrame(frame);
  }, []);

  useEffect(() => {
    let rafId;
    const animate = () => {
      pos.current.x += (target.current.x - pos.current.x) * 0.12;
      pos.current.y += (target.current.y - pos.current.y) * 0.12;

      for (let i = 0; i < 3; i++) {
        const tgt = i === 0 ? pos.current : trailPos.current[i - 1];
        trailPos.current[i].x += (tgt.x - trailPos.current[i].x) * (0.1 + i * 0.02);
        trailPos.current[i].y += (tgt.y - trailPos.current[i].y) * (0.1 + i * 0.02);
      }

      const cx = pos.current.x;
      const cy = pos.current.y;

      if (canvasRef.current) {
        canvasRef.current.style.transform = `translate(${cx - 14}px, ${cy - 14}px)`;
        const ctx = canvasRef.current.getContext('2d');
        const w = 28, h = 28;
        ctx.clearRect(0, 0, w, h);

        const hoverMul = isHovering.current ? 4 : 1;
        angles.current.x += 0.008 * hoverMul;
        angles.current.y += 0.012 * hoverMul;
        angles.current.z += 0.004 * hoverMul;

        const ax = angles.current.x;
        const ay = angles.current.y;
        const az = angles.current.z;

        const rotated = MERKABA_VERTICES.map(v => {
          let y = v[1] * Math.cos(ax) - v[2] * Math.sin(ax);
          let z = v[1] * Math.sin(ax) + v[2] * Math.cos(ax);
          let x = v[0];
          let x2 = x * Math.cos(ay) + z * Math.sin(ay);
          z = -x * Math.sin(ay) + z * Math.cos(ay);
          x = x2;
          x2 = x * Math.cos(az) - y * Math.sin(az);
          y = x * Math.sin(az) + y * Math.cos(az);
          x = x2;
          return [x, y, z];
        });

        const scale = 7;
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 0.8;
        ctx.shadowColor = 'rgba(255, 255, 255, 0.4)';
        ctx.shadowBlur = 4;

        MERKABA_EDGES.forEach(([i, j]) => {
          ctx.beginPath();
          const p1 = rotated[i];
          const p2 = rotated[j];
          const persp = 3;
          const d1 = persp / (persp + p1[2]);
          const d2 = persp / (persp + p2[2]);
          ctx.moveTo(p1[0] * scale * d1 + w / 2, p1[1] * scale * d1 + h / 2);
          ctx.lineTo(p2[0] * scale * d2 + w / 2, p2[1] * scale * d2 + h / 2);
          ctx.stroke();
        });
      }

      trailEls.current.forEach((el, i) => {
        if (el) {
          const size = 6 - i * 1.5;
          el.style.transform = `translate(${trailPos.current[i].x - size / 2}px, ${trailPos.current[i].y - size / 2}px)`;
          el.style.width = `${size}px`;
          el.style.height = `${size}px`;
          el.style.opacity = Math.max(0.2 - i * 0.06, 0.05);
        }
      });

      speed.current *= 0.88;
      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);

    const handleLinkOver = e => {
      isHovering.current = e.target.closest('a, button') !== null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseover', handleLinkOver);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseover', handleLinkOver);
    };
  }, [handleMouseMove, handleMouseDown]);

  return (
    <>
      <canvas ref={canvasRef} className="custom-cursor" width={28} height={28} />
    </>
  );
}

export default CursorFollower;
